import React, { useState } from 'react';
import {
  VStack, Text, HStack, Switch,
} from 'native-base';
import { Button, Chip, TextInput } from 'react-native-paper';
import { DatePickerInput, TimePickerModal } from 'react-native-paper-dates';
import { ScrollView, View } from 'react-native';
import Picker from '../../components/commons/Picker';
import AutocompleteLocation from '../../components/commons/AutocompleteLocation';
import {
  DOCUMENT_TYPE, DRIVER_DOCUMENT_TYPES,
} from '../../screens/Profile/ProfileTabs/DocumentTab';

export const getMissingDriverDocuments = (driver) => {
  const provided = driver?.profile?.files?.map((f) => f.type) ?? [];
  return DRIVER_DOCUMENT_TYPES
    .filter((t) => !provided.includes(t))
    .map((t) => DOCUMENT_TYPE[t].name);
};

const formatTime = (date) => {
  if (!date) return '--:--';
  const d = date instanceof Date ? date : new Date(date);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const setTimeOnDate = (date, hours, minutes) => {
  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return d;
};

export function FormStepOne({
  values, errors, touched, setFieldValue, openPicker, setOpenPicker,
  drivers, transportations, driverGroups,
}) {
  const [showDepartureTime, setShowDepartureTime] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const selectedDriver = values.driverId
    ? drivers.find((d) => d.id === values.driverId)
    : null;
  const selectedDriverMissingDocuments = selectedDriver
    ? getMissingDriverDocuments(selectedDriver)
    : null;

  const groupMemberIds = selectedGroupId
    ? new Set(
      (driverGroups ?? [])
        .find((g) => g.id === selectedGroupId)
        ?.members.map((m) => m.userId) ?? [],
    )
    : null;

  const filteredDrivers = groupMemberIds
    ? drivers.filter((d) => groupMemberIds.has(d.id))
    : drivers;

  const getDriverLabel = (d) => {
    if (!driverGroups) return `${d.firstName} ${d.lastName}`;
    const groups = (driverGroups).filter((g) => g.members.some((m) => m.userId === d.id));
    if (groups.length === 0) return `${d.firstName} ${d.lastName}`;
    return `${d.firstName} ${d.lastName} (${groups.map((g) => g.name).join(', ')})`;
  };

  const handleDepartureTimeConfirm = ({ hours, minutes }) => {
    setShowDepartureTime(false);
    const newDeparture = setTimeOnDate(values.departureDateTime, hours, minutes);
    setFieldValue('departureDateTime', newDeparture);
  };

  const handleDepartureDateChange = (d) => {
    if (!d) return;
    const current = values.departureDateTime;
    const merged = setTimeOnDate(d, current.getHours(), current.getMinutes());
    setFieldValue('departureDateTime', merged);
  };

  return (
    <VStack space={4}>
      <VStack zIndex={3000}>
        <Text mb={2}>Transport</Text>
        <Picker
          open={openPicker === 'transport'}
          setOpen={(isOpen) => setOpenPicker(isOpen ? 'transport' : null)}
          selectedValue={values.transportationId}
          onValueChange={(value) => setFieldValue('transportationId', value)}
          placeholder="Sélectionnez un transport"
          zIndex={3000}
        >
          {transportations.map((t) => (
            <Picker.Item key={t.id} label={t.name} value={t.id} />
          ))}
        </Picker>
        {!!errors.transportationId && <Text color="red.500">{errors.transportationId}</Text>}
      </VStack>

      <VStack zIndex={2000}>
        <Text mb={2}>Conducteur</Text>
        {driverGroups && driverGroups.length > 0 && (
          <View style={{
            flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 8,
          }}
          >
            <Chip
              selected={selectedGroupId === null}
              onPress={() => setSelectedGroupId(null)}
            >
              Tous
            </Chip>
            {driverGroups.map((g) => (
              <Chip
                key={g.id}
                selected={selectedGroupId === g.id}
                onPress={() => setSelectedGroupId(selectedGroupId === g.id ? null : g.id)}
              >
                {g.name}
              </Chip>
            ))}
          </View>
        )}
        <Picker
          open={openPicker === 'driver'}
          setOpen={(isOpen) => setOpenPicker(isOpen ? 'driver' : null)}
          selectedValue={values.driverId}
          onValueChange={(value) => setFieldValue('driverId', value)}
          zIndex={2000}
          placeholder="Sélectionnez un conducteur"
        >
          {filteredDrivers.map((d) => (
            <Picker.Item key={d.id} label={getDriverLabel(d)} value={d.id} />
          ))}
        </Picker>
        {!!errors.driverId && <Text color="red.500">{errors.driverId}</Text>}
        {selectedDriverMissingDocuments?.length === 0 && (
          <HStack alignItems="center" space={1} mt={1}>
            <Text fontSize="sm" color="green.600">✓ Documents conducteur complets</Text>
          </HStack>
        )}
        {selectedDriverMissingDocuments?.length > 0 && (
          <VStack mt={1}>
            <Text fontSize="sm" color="red.500">
              Ce conducteur ne peut pas être assigné : documents manquants
            </Text>
            <Text fontSize="xs" color="red.500">
              {selectedDriverMissingDocuments.join(', ')}
            </Text>
          </VStack>
        )}
      </VStack>

      <VStack>
        <Text mb={1}>Lieu de départ</Text>
        <AutocompleteLocation
          value={values.departureLocation}
          handleChangeText={(val) => setFieldValue('departureLocation', val)}
          handleSubmit={(val) => setFieldValue('departureLocation', val)}
        />
        {!!errors.departureLocation && <Text color="red.500">{errors.departureLocation}</Text>}
      </VStack>

      <DatePickerInput
        label="Date de départ"
        value={values.departureDateTime}
        onChange={handleDepartureDateChange}
        locale="fr"
        mode="outlined"
      />
      <Button
        mode="outlined"
        icon="clock-outline"
        onPress={() => setShowDepartureTime(true)}
      >
        {`Heure de départ: ${formatTime(values.departureDateTime)}`}
      </Button>
      <TimePickerModal
        visible={showDepartureTime}
        onDismiss={() => setShowDepartureTime(false)}
        onConfirm={handleDepartureTimeConfirm}
        hours={values.departureDateTime?.getHours()}
        minutes={values.departureDateTime?.getMinutes()}
        label="Heure de départ"
        cancelLabel="Annuler"
        confirmLabel="Confirmer"
        use24HourClock
      />
      {!!errors.departureDateTime && <Text color="red.500">{errors.departureDateTime}</Text>}

      <VStack>
        <Text mb={1}>Lieu d'arrivée</Text>
        <AutocompleteLocation
          value={values.arrivalLocation}
          handleChangeText={(val) => setFieldValue('arrivalLocation', val)}
          handleSubmit={(val) => setFieldValue('arrivalLocation', val)}
        />
        {!!errors.arrivalLocation && <Text color="red.500">{errors.arrivalLocation}</Text>}
      </VStack>

      <HStack alignItems="center" space={2}>
        <Text>Séjour à l'hôtel</Text>
        <Switch
          isChecked={values.hasHotelStay}
          onToggle={() => setFieldValue('hasHotelStay', !values.hasHotelStay)}
        />
      </HStack>

      {values.hasHotelStay && (
        <>
          <TextInput
            label="Nom de l'hôtel"
            value={values.hotelName}
            onChangeText={(val) => setFieldValue('hotelName', val)}
            mode="outlined"
          />

          <VStack>
            <Text mb={1}>Lieu de l'hôtel</Text>
            <AutocompleteLocation
              value={values.hotelLocation}
              handleChangeText={(val) => setFieldValue('hotelLocation', val)}
              handleSubmit={(val) => setFieldValue('hotelLocation', val)}
            />
          </VStack>
        </>
      )}
    </VStack>
  );
}
